import type { FirebaseConfigEnv } from 'shared/services/firebaseConfig';

export type NodeEnvironment = 'development' | 'production' | 'test';

export interface ClientEnvironment {
  readonly firebase: FirebaseConfigEnv;
  readonly useEmulators: boolean;
  readonly firestoreDatabaseId: string;
  readonly authEmulatorPort?: string;
  readonly firestoreEmulatorPort?: string;
  readonly storageEmulatorPort?: string;
  readonly functionsUrl: string;
  readonly syntheticDataCoordinatorUrl: string;
  readonly retainerAccountEmail: string;
  readonly buildId: string;
  readonly buildTime: string;
  readonly buildGitSha: string;
  readonly nodeEnvironment: NodeEnvironment;
  readonly isProduction: boolean;
}

export type RawClientEnvironment = Record<string, string | undefined>;

declare global {
  // Vite replaces this property with an allowlisted object at build/dev time.
  // Jest assigns process.env to it from setupTests.ts so test-local env changes
  // remain observable by the getter-backed adapter.
  var __ALIGNED_CLIENT_ENV__: RawClientEnvironment | undefined;
}

function buildFirebaseEnvironment(raw: RawClientEnvironment): FirebaseConfigEnv {
  return {
    REACT_APP_FIREBASE_API_KEY: raw.REACT_APP_FIREBASE_API_KEY,
    REACT_APP_FIREBASE_AUTH_DOMAIN: raw.REACT_APP_FIREBASE_AUTH_DOMAIN,
    REACT_APP_FIREBASE_PROJECT_ID: raw.REACT_APP_FIREBASE_PROJECT_ID,
    REACT_APP_FIREBASE_STORAGE_BUCKET: raw.REACT_APP_FIREBASE_STORAGE_BUCKET,
    REACT_APP_FIREBASE_MESSAGING_SENDER_ID: raw.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    REACT_APP_FIREBASE_APP_ID: raw.REACT_APP_FIREBASE_APP_ID,
    REACT_APP_AUTH_EMULATOR_PORT: raw.REACT_APP_AUTH_EMULATOR_PORT,
    REACT_APP_FIRESTORE_EMULATOR_PORT: raw.REACT_APP_FIRESTORE_EMULATOR_PORT,
    REACT_APP_STORAGE_EMULATOR_PORT: raw.REACT_APP_STORAGE_EMULATOR_PORT,
  };
}

function resolveNodeEnvironment(value: string | undefined): NodeEnvironment {
  if (value === 'production' || value === 'test') return value;
  return 'development';
}

export function createClientEnvironment(raw: RawClientEnvironment): ClientEnvironment {
  return {
    get firebase() {
      return buildFirebaseEnvironment(raw);
    },
    get useEmulators() {
      return raw.REACT_APP_USE_EMULATORS === 'true';
    },
    get firestoreDatabaseId() {
      return raw.REACT_APP_FIRESTORE_DATABASE_ID || '(default)';
    },
    get authEmulatorPort() {
      return raw.REACT_APP_AUTH_EMULATOR_PORT;
    },
    get firestoreEmulatorPort() {
      return raw.REACT_APP_FIRESTORE_EMULATOR_PORT;
    },
    get storageEmulatorPort() {
      return raw.REACT_APP_STORAGE_EMULATOR_PORT;
    },
    get functionsUrl() {
      return raw.REACT_APP_FUNCTIONS_URL || '';
    },
    get syntheticDataCoordinatorUrl() {
      return raw.REACT_APP_SYNTHETIC_DATA_COORDINATOR_URL?.trim() || '';
    },
    get retainerAccountEmail() {
      return raw.REACT_APP_RETAINER_ACCOUNT_EMAIL?.trim().toLowerCase() || 'paul@alignedhq.ai';
    },
    get buildId() {
      return raw.REACT_APP_BUILD_ID || 'dev';
    },
    get buildTime() {
      return raw.REACT_APP_BUILD_TIME || '';
    },
    get buildGitSha() {
      return raw.REACT_APP_GIT_SHA || '';
    },
    get nodeEnvironment() {
      return resolveNodeEnvironment(raw.NODE_ENV);
    },
    get isProduction() {
      return this.nodeEnvironment === 'production';
    },
  };
}

export const clientEnvironment = createClientEnvironment(globalThis.__ALIGNED_CLIENT_ENV__ ?? {});
